import { Calendar, Umbrella, Heart, Clock, AlertCircle } from 'lucide-react';
import { PTOAllocation } from '../../services/time-off.service';

interface PTOAllocationCardProps {
  allocation: PTOAllocation;
}

export default function PTOAllocationCard({ allocation }: PTOAllocationCardProps) {
  const ptoTypes = [
    {
      label: 'Vacation',
      icon: Umbrella,
      color: 'blue',
      allocated: allocation.vacationDays,
      used: allocation.vacationUsed,
      remaining: allocation.vacationRemaining,
    },
    {
      label: 'Sick',
      icon: Heart,
      color: 'green',
      allocated: allocation.sickDays,
      used: allocation.sickUsed,
      remaining: allocation.sickRemaining,
    },
    {
      label: 'Personal',
      icon: Clock,
      color: 'purple',
      allocated: allocation.personalDays,
      used: allocation.personalUsed,
      remaining: allocation.personalRemaining,
    },
    {
      label: 'Bereavement',
      icon: AlertCircle,
      color: 'gray',
      allocated: allocation.bereavementDays,
      used: allocation.bereavementUsed,
      remaining: allocation.bereavementRemaining,
    },
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; border: string; progress: string }> = {
      blue: {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200',
        progress: 'bg-blue-500',
      },
      green: {
        bg: 'bg-green-50',
        text: 'text-green-700',
        border: 'border-green-200',
        progress: 'bg-green-500',
      },
      purple: {
        bg: 'bg-purple-50',
        text: 'text-purple-700',
        border: 'border-purple-200',
        progress: 'bg-purple-500',
      },
      gray: {
        bg: 'bg-gray-50',
        text: 'text-gray-700',
        border: 'border-gray-200',
        progress: 'bg-gray-500',
      },
    };
    return colors[color];
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calendar className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Time Off Balance</h3>
            <p className="text-sm text-gray-600">Year {allocation.year}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-600">Total Remaining</p>
          <p className="text-3xl font-bold text-gray-900">{allocation.totalRemaining}</p>
          <p className="text-xs text-gray-500">of {allocation.totalAllocated} days</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {ptoTypes.map((type) => {
          const colors = getColorClasses(type.color);
          const Icon = type.icon;
          const usagePercent = type.allocated > 0 ? (type.used / type.allocated) * 100 : 0;

          return (
            <div
              key={type.label}
              className={`p-4 rounded-lg border ${colors.bg} ${colors.border}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Icon className={`h-5 w-5 ${colors.text}`} />
                  <span className={`font-medium ${colors.text}`}>{type.label}</span>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold ${colors.text}`}>{type.remaining}</p>
                  <p className="text-xs text-gray-600">days left</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Used: {type.used}</span>
                  <span>Total: {type.allocated}</span>
                </div>
                <div className="w-full bg-white rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-2 rounded-full transition-all ${colors.progress}`}
                    style={{ width: `${Math.min(usagePercent, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
